import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ProfileData } from "./useProfileSettings";
import { syncUserEmail } from "@/utils/profileUtils";

export const useProfileFetch = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  
  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      
      // Check if current page is a public page (like /services) where authentication is not required
      const isPublicPage = window.location.pathname.includes('/services');

      if (isPublicPage) {
        // For public pages, we can return early without an error
        console.log("Public page accessed without authentication");
        setLoading(false);
        return null;
      }
      
      // Get user information from Supabase
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error("Error fetching user:", userError);
        throw userError;
      }
      
      if (!user) {
        // If we're after onboarding or a redirect, the user may not be immediately available
        // Schedule a retry in a short time
        if (retryCount < 3) {
          console.log(`User not available yet, scheduling retry (${retryCount + 1}/3)...`);
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 500); // Retry after 500ms
        } else {
          console.error("User not authenticated after multiple retries");
        }
        
        return null;
      }
      
      console.log("Fetching profile for user ID:", user.id);
      
      // Reset retry count once user is available
      if (retryCount > 0) {
        setRetryCount(0);
      }
      
      // Ensure email is synchronized before fetching profile
      if (user.email) {
        const { success, message } = await syncUserEmail(user.id, user.email);
        console.log("Email sync result:", message);
      }
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (error) {
        console.error("Error fetching profile:", error);
        throw error;
      }
      
      console.log("Profile data:", data);
      
      const profileData = {
        ...data,
        email: user.email // Always use email from auth to ensure it's the most up-to-date
      };
      
      setProfile(profileData);
      
      // Call edge function to update completeness after login/profile fetch
      try {
        const { getAuthToken } = await import("@/lib/auth");
        const { updateProfileCompleteness } = await import("@/hooks/useProfileSettings");
        const accessToken = await getAuthToken();
        if (user.id && accessToken) {
          updateProfileCompleteness(user.id, accessToken);
        }
      } catch (e) {
        console.error("Failed to call updateProfileCompleteness after login:", e);
      }
      
      return profileData;
    } catch (error) {
      console.error("Error in fetchProfile:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load profile data. Please refresh the page.",
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast, retryCount]);
  
  // Fetch the profile initially and whenever retry count changes
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile, retryCount]);
  
  return {
    loading,
    profile,
    fetchProfile,
    setProfile,
  };
};
